import exifr from 'exifr';

const HEIC_EXTENSION_PATTERN = /\.hei[cf]$/i;

// Client-only — deliberately not part of MediaImageContentTypeEnum, which
// also feeds the server's MediaContentType scalar allowlist. We never
// persist media with one of these as its final contentType; every HEIC
// upload is converted to image/jpeg before it's stored.
export const HEIC_MIME_TYPES = ['image/heic', 'image/heif'] as const;

export function isHeicFile(file: File): boolean {
  return (
    (HEIC_MIME_TYPES as readonly string[]).includes(file.type.toLowerCase()) ||
    HEIC_EXTENSION_PATTERN.test(file.name)
  );
}

// The file is always converted to JPEG before it's stored — the displayed
// name should reflect that, not the original .heic/.heif extension.
export function renameHeicExtensionToJpeg(fileName: string): string {
  return fileName.replace(HEIC_EXTENSION_PATTERN, '.jpg');
}

// Read client-side before the file is handed off for HEIC conversion —
// GPS is deliberately not carried over (privacy), and no Orientation tag
// is re-added since the decoder already bakes rotation into the pixels.
export async function readOriginalDateTaken(
  file: File
): Promise<Date | undefined> {
  try {
    const exif = await exifr.parse(file, ['DateTimeOriginal']);

    return exif?.DateTimeOriginal instanceof Date
      ? exif.DateTimeOriginal
      : undefined;
  } catch {
    return undefined;
  }
}

// exifr revives DateTimeOriginal via the browser's local Date constructor,
// so its local getters give back the exact raw EXIF wall-clock digits.
// Formatting directly here (instead of round-tripping through an ISO
// string) avoids any timezone math on either side — the server writes
// this string into EXIF as-is, no Date parsing involved.
export function formatDateTakenForExif(dateTaken: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${dateTaken.getFullYear()}:${pad(dateTaken.getMonth() + 1)}:${pad(
    dateTaken.getDate()
  )} ${pad(dateTaken.getHours())}:${pad(dateTaken.getMinutes())}:${pad(
    dateTaken.getSeconds()
  )}`;
}
